import { customMerge } from '@/utils/array';
import {
  DynamicConfig,
  LineItemNotesAccessMode,
  OnDeviceScanningSearchType,
  OrderSummarySummationOptions,
} from '@/contexts/dynamic-config';
import { mergeWith } from 'lodash';
import { NOTES_MAX_LENGTH } from '@/components/order-guide/constants';
import { ViewableBy } from '@/enums';

export const DEFAULT_BUTTON_TEXT_FIELDS = [
  OrderSummarySummationOptions.SUBTOTAL,
];
export const DEFAULT_SECTION_HEADER_FIELDS = [
  OrderSummarySummationOptions.UNITS,
];

export const defaultDynamicConfigValues: {
  [key in keyof DynamicConfig]: DynamicConfig[key];
} = {
  account_selector_customer_group_filter_enabled: false,
  assigned_sales_rep_visible: false,
  autopay_enabled: false,
  autopay_self_serve_enabled: false,
  availability_status_config: {
    enabled: false,
    enabled_statuses_for_operator_employees: [],
    enabled_statuses_for_supplier_employees: {},
    sort_disabled_statuses_last: false,
  },
  brand_pages_enabled: false,
  card_view_v2_enabled: false,
  cash_payments_enabled: false,
  catalog_filtering_enabled: false,
  catalog_group_items_expanded_enabled: false,
  catalog_lazy_carousels_enabled: false,
  catalog_search_ux_enabled: true,
  category_images_enabled: false,
  credit_request_config: {
    credit_policy: [],
    display_guarantee_information: false,
    print_credit_request_enabled: false,
    sort_reasons_alphabetically: false,
    viewable_by: ViewableBy.ALL,
  },
  disable_pepper_invoice_pdf: false,
  experiments: [],
  invoice_pdf_visibility_config: {
    hidden_for: {
      distributor: [],
      operator: [],
    },
  },
  item_groups_enabled: false,
  item_subscriptions_enabled: false,
  jack_henry_checks_config: {
    check_capture_enabled: false,
  },
  landing_page_enabled: true,
  limited_app_enabled: false,
  line_item_notes_config: {
    access_mode: LineItemNotesAccessMode.ALL,
    enabled: false,
    max_length: NOTES_MAX_LENGTH,
  },
  list_item_display_config: {
    enabled: false,
  },
  manual_check_payments_enabled: false,
  offline_mode_config: {
    catalog_enabled: false,
    foreground_sync_enabled: false,
    order_queuing_disabled: false,
  },
  offline_mode_enabled: false,
  on_device_scanning_config: {
    additional_product_info_enabled: false,
    default_matching_pack_unit: null,
    local_scan_ux_enabled: false,
    local_scan_ux_images_enabled: false,
    prominent_scanning_entry_enabled: false,
    search_type: OnDeviceScanningSearchType.PARTIAL_MATCH,
  },
  order_copying_config: {
    enabled_restaurant_roles: [],
    enabled_supplier_roles: [],
  },
  order_guide_catalog_search_enabled: true,
  order_guide_unified_search_enabled: false,
  order_quantity_config: {
    enforce_for_supplier_admins: true,
    min_max_quantity_enabled: false,
    min_max_quantity_v2_enabled: false,
  },
  order_summary_config: {
    button_text_fields: DEFAULT_BUTTON_TEXT_FIELDS,
    section_header_fields: DEFAULT_SECTION_HEADER_FIELDS,
  },
  pack_sort_config: {},
  performance_tracing_config: {
    emit_start_events: true,
    enabled: false,
    sample_rate: 0,
  },
  print_order_enabled: false,
  print_par_sheet_enabled: false,
  promotions_config: {},
  reset_fulfillment_on_order_submission: false,
  salt_api_integration_enabled: false,
  salt_cache_read_enabled: false,
  search_config: {
    order_guide: {
      default_filters: [],
    },
    subcategories_filter_enabled: true,
  },
  sentry_config: {
    tracing: {
      enabled: true,
      restaurant_app: {
        enabled: true,
        sample_rate: 0.01,
      },
    },
  },
  smart_order_config: {
    email_threads_enabled: false,
    inbound_history_enabled: false,
    separate_employee_drafts_enabled: false,
    surface_notes_enabled: false,
  },
  sort_categories_with_special_chars_last: false,
  tab_card_config: {
    cashback_percentage: null,
    enabled: false,
    sign_up_url: null,
  },
  tag_printing_config: {
    batch_delivery_enabled: false,
    batch_tag_printing_enabled: false,
    custom_tag_printing_enabled: false,
    generic_tag_printing_enabled: false,
    order_printing_enabled: false,
    tag_printing_enabled: false,
  },
  timeslot_cutoff_fulfillment_hydration_enabled: false,
  typesense_config: {
    catalog_category_lists: false,
    filtering_enabled: false,
  },
};

export const getValueFromConfigs = <T extends keyof DynamicConfig>(
  key: T,
  supplierDynamicConfig?: DynamicConfig,
  chatDynamicConfig?: DynamicConfig,
  defaultValue?: DynamicConfig[T],
) => {
  const defaultConfig = defaultValue ?? defaultDynamicConfigValues[key];
  const supplierConfig = supplierDynamicConfig?.[key];
  const chatConfig = chatDynamicConfig ? chatDynamicConfig[key] : null;

  if (typeof defaultConfig === 'object') {
    const mergedResult = mergeWith(
      {},
      defaultConfig,
      supplierConfig ?? {},
      chatConfig ?? {},
      customMerge,
    );

    return mergedResult;
  }

  return chatConfig ?? supplierConfig ?? defaultConfig;
};
